import type { CSSProperties } from "react";

/**
 * Uma escala só de breakpoints, e uma forma só de declarar valor responsivo.
 *
 * A v1 tinha três convenções no mesmo pacote: `ResponsiveProp<T>` no Grid,
 * props planas irmãs (`base`, `sm`, `md`…) nos spans do `Col`, e objeto
 * aninhado nos offsets dele. Aqui é uma.
 *
 * Os valores são os do Tailwind, que é a escala que o resto da biblioteca já
 * usa. A v1 usava 576/768/992/1200 — só o `md` coincide. A diferença é
 * deliberada e está no MIGRATION.md: um `Col sm={6}` vira a coluna em outra
 * largura.
 */
export const BREAKPOINTS = {
  sm: "40rem", // 640px
  md: "48rem", // 768px
  lg: "64rem", // 1024px
  xl: "80rem", // 1280px
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/** Um valor para todas as larguras, ou um por breakpoint. */
export type ResponsiveProp<T> = T | ({ base?: T } & Partial<Record<Breakpoint, T>>);

const KEYS = ["base", "sm", "md", "lg", "xl"] as const;

/**
 * Monta o objeto de `style` com custom properties, descartando o que não foi
 * informado.
 *
 * Descartar importa: uma variável ausente faz o `var()` cair no próprio
 * fallback, que é como a cascata mobile-first acontece dentro do valor em vez
 * de depender de especificidade. Uma variável presente com `undefined`
 * quebraria isso.
 */
export function buildCssVars(vars: Record<string, string | number | undefined>): CSSProperties {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(vars)) {
    if (value !== undefined && value !== null && value !== "") out[key] = value;
  }
  return out as CSSProperties;
}

/**
 * Espalha um valor responsivo em `--ds2-<prefix>-<breakpoint>`.
 *
 * Portado do `getResponsiveVariables` da v1, que vivia duplicado em
 * `GridContainer` e `GridItem`. Duas diferenças: as chaves são validadas
 * contra `KEYS` — na v1 um `{ tablet: … }` gerava uma variável morta em
 * silêncio — e `0` é preservado, porque a guarda da v1 era truthiness e
 * engolia `base={0}`.
 */
export function responsiveVars<T extends string | number>(
  value: ResponsiveProp<T> | undefined,
  prefix: string,
): CSSProperties {
  if (value === undefined || value === null) return {};

  if (typeof value !== "object") {
    return buildCssVars({ [`--ds2-${prefix}-base`]: value });
  }

  const vars: Record<string, string | number | undefined> = {};
  for (const key of KEYS) {
    vars[`--ds2-${prefix}-${key}`] = (value as Record<string, T | undefined>)[key];
  }
  return buildCssVars(vars);
}
